import type { ButtonHTMLAttributes, ReactNode } from "react";

/**
 * Taxonomia de botões da V2 (AGENTS §13.1).
 *
 * `primary` é a ação principal da superfície e é a ÚNICA que recebe brilho no
 * hover e bounce no clique — os dois vivem em `components.css`, nunca no
 * componente que usa o botão. Nenhuma variante ganha partículas.
 *
 * `loading` bloqueia o clique de verdade, não só visualmente: envio duplicado é
 * regressão de domínio (§26.2), não detalhe de estilo.
 */
export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "text";
export type ButtonSize = "default" | "compact";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Ícone à esquerda do rótulo. Decorativo: o nome acessível vem do texto. */
  icon?: ReactNode;
  /** Ícone à direita, para menus e divulgação progressiva. */
  trailingIcon?: ReactNode;
  loading?: boolean;
  type?: "button" | "submit" | "reset";
  children?: ReactNode;
}

export function Button({
  variant = "secondary",
  size = "default",
  icon,
  trailingIcon,
  loading = false,
  type = "button",
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      className={className ? `cx-btn ${className}` : "cx-btn"}
      data-variant={variant}
      data-size={size}
      data-loading={loading ? "true" : undefined}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {loading ? (
        <span className="cx-btn-spinner" aria-hidden="true" />
      ) : (
        icon && <span aria-hidden="true">{icon}</span>
      )}
      {children}
      {trailingIcon && <span aria-hidden="true">{trailingIcon}</span>}
    </button>
  );
}
